import React, { useMemo, useState, useCallback, useRef } from 'react';
import Plot from 'react-plotly.js';
import { useData } from '../../context/DataContext';
import { disperseAnnotations } from '../../utils/plotUtils';

const COMPARISONS = [
  { id: 'p0_vs_p6', label: 'P0 \u2192 P6 (Transformation)' },
  { id: 'p6_vs_taz', label: 'P6 \u2192 P6+Taz (EZH2i)' },
  { id: 'p0_vs_taz', label: 'P0 \u2192 P6+Taz (Net effect)' },
];

const SENS_COLORS = {
  EZH2_sensitive: '#27ae60',
  EZH2_insensitive: '#e67e22',
};

const FDR_OPTIONS = [0.001, 0.01, 0.05, 0.1];

export default function TransformationVolcano({ sensitivity, onSelectGene }) {
  const { data, resolveGene } = useData();
  const [selectedComp, setSelectedComp] = useState('p0_vs_p6');
  const [fcCutoff, setFcCutoff] = useState(1);
  const [fdrCutoff, setFdrCutoff] = useState(0.05);
  const [toggledLabels, setToggledLabels] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [colorBySens, setColorBySens] = useState(true);
  const searchRef = useRef(null);

  const compData = data?.deResults?.[selectedComp];

  const plotData = useMemo(() => {
    if (!compData?.genes) return null;

    const genes = [], log2fc = [], fdr = [], negLogFdr = [];
    const minFdr = 1e-16;

    for (const [gene, d] of Object.entries(compData.genes)) {
      genes.push(gene);
      log2fc.push(d.log2FC);
      fdr.push(d.fdr);
      negLogFdr.push(-Math.log10(Math.max(d.fdr, minFdr)));
    }

    // Categorize
    const ns = [], sigSens = [], sigInsens = [], sigUp = [], sigDown = [];
    for (let i = 0; i < genes.length; i++) {
      const absFc = Math.abs(log2fc[i]);
      if (absFc < fcCutoff || fdr[i] > fdrCutoff) { ns.push(i); continue; }

      if (colorBySens && sensitivity) {
        const sens = sensitivity[genes[i]]?.sens;
        if (sens === 'EZH2_sensitive') sigSens.push(i);
        else if (sens === 'EZH2_insensitive') sigInsens.push(i);
        else if (log2fc[i] > 0) sigUp.push(i);
        else sigDown.push(i);
      } else {
        if (log2fc[i] > 0) sigUp.push(i);
        else sigDown.push(i);
      }
    }

    // Downsample NS
    let nsSubset = ns;
    if (ns.length > 4000) {
      const step = Math.ceil(ns.length / 4000);
      nsSubset = ns.filter((_, i) => i % step === 0);
    }

    // Top genes
    const scored = genes.map((g, i) => ({ gene: g, idx: i, score: negLogFdr[i] * Math.abs(log2fc[i]) }));
    scored.sort((a, b) => b.score - a.score);
    const topGenes = scored.slice(0, 15);

    return { genes, log2fc, fdr, negLogFdr, ns: nsSubset, sigSens, sigInsens, sigUp, sigDown, topGenes };
  }, [compData, fcCutoff, fdrCutoff, sensitivity, colorBySens]);

  const handleSearchInput = useCallback((e) => {
    const q = e.target.value;
    setSearchQuery(q);
    setSearchResult(null);
    if (q.length >= 2 && data?.geneList) {
      const lower = q.toLowerCase();
      setSuggestions(data.geneList.filter(g => g.toLowerCase().startsWith(lower)).slice(0, 8));
    } else {
      setSuggestions([]);
    }
  }, [data]);

  const doSearch = useCallback((geneName) => {
    const resolved = resolveGene(geneName);
    if (resolved && compData?.genes[resolved]) {
      setSearchResult({ gene: resolved, found: true });
      setSearchQuery(resolved);
    } else {
      setSearchResult({ gene: geneName, found: false });
    }
    setSuggestions([]);
  }, [resolveGene, compData]);

  const handleClick = useCallback((e) => {
    if (e.points?.[0]) {
      const pt = e.points[0];
      const gene = pt.text || pt.hovertext;
      const cleanGene = gene?.replace(/<[^>]+>/g, '').split('\n')[0].trim();
      if (cleanGene) {
        setToggledLabels(prev => {
          const next = new Set(prev);
          if (next.has(cleanGene)) { next.delete(cleanGene); if (onSelectGene) onSelectGene(cleanGene); }
          else next.add(cleanGene);
          return next;
        });
      }
    }
  }, [onSelectGene]);

  if (!data?.deResults) return <div className="plot-placeholder">Loading volcano data...</div>;
  if (!plotData) return <div className="plot-placeholder">No data for selected comparison</div>;

  const { genes, log2fc, fdr: fdrArr, negLogFdr, ns, sigSens, sigInsens, sigUp, sigDown, topGenes } = plotData;
  const plotWidth = 880, plotHeight = 620;
  const margin = { l: 70, r: 30, t: 50, b: 70 };

  const makeHover = (i) => `<b>${genes[i]}</b><br>FC: ${log2fc[i].toFixed(2)}<br>FDR: ${fdrArr[i].toExponential(2)}`;

  const traces = [
    {
      type: 'scattergl', mode: 'markers',
      x: ns.map(i => log2fc[i]), y: ns.map(i => negLogFdr[i]),
      marker: { size: 3, color: '#bbb', opacity: 0.4 },
      hovertext: ns.map(i => genes[i]), hoverinfo: 'text',
      name: `NS (${ns.length})`, text: ns.map(i => genes[i]),
    },
  ];

  if (colorBySens && sensitivity) {
    if (sigSens.length > 0) traces.push({
      type: 'scattergl', mode: 'markers',
      x: sigSens.map(i => log2fc[i]), y: sigSens.map(i => negLogFdr[i]),
      marker: { size: 5, color: SENS_COLORS.EZH2_sensitive, opacity: 0.7 },
      hovertext: sigSens.map(makeHover), hoverinfo: 'text',
      name: `EZH2-sensitive (${sigSens.length})`, text: sigSens.map(i => genes[i]),
    });
    if (sigInsens.length > 0) traces.push({
      type: 'scattergl', mode: 'markers',
      x: sigInsens.map(i => log2fc[i]), y: sigInsens.map(i => negLogFdr[i]),
      marker: { size: 5, color: SENS_COLORS.EZH2_insensitive, opacity: 0.7 },
      hovertext: sigInsens.map(makeHover), hoverinfo: 'text',
      name: `EZH2-insensitive (${sigInsens.length})`, text: sigInsens.map(i => genes[i]),
    });
  }

  if (sigUp.length > 0) traces.push({
    type: 'scattergl', mode: 'markers',
    x: sigUp.map(i => log2fc[i]), y: sigUp.map(i => negLogFdr[i]),
    marker: { size: 5, color: '#c0392b', opacity: 0.7 },
    hovertext: sigUp.map(makeHover), hoverinfo: 'text',
    name: `Up in ${compData.labelA} (${sigUp.length})`, text: sigUp.map(i => genes[i]),
  });
  if (sigDown.length > 0) traces.push({
    type: 'scattergl', mode: 'markers',
    x: sigDown.map(i => log2fc[i]), y: sigDown.map(i => negLogFdr[i]),
    marker: { size: 5, color: '#2471a3', opacity: 0.7 },
    hovertext: sigDown.map(makeHover), hoverinfo: 'text',
    name: `Up in ${compData.labelB} (${sigDown.length})`, text: sigDown.map(i => genes[i]),
  });

  // Search highlight
  if (searchResult?.found) {
    const d = compData.genes[searchResult.gene];
    if (d) {
      const y = -Math.log10(Math.max(d.fdr, 1e-16));
      traces.push({
        type: 'scatter', mode: 'markers',
        x: [d.log2FC], y: [y],
        marker: { size: 14, color: '#f1c40f', symbol: 'diamond', line: { width: 2, color: '#1a1a1a' } },
        hovertext: [`<b>${searchResult.gene}</b><br>FC: ${d.log2FC.toFixed(2)}<br>FDR: ${d.fdr.toExponential(2)}`],
        hoverinfo: 'text', name: searchResult.gene, showlegend: false,
      });
    }
  }

  // Annotations
  const allLabeled = new Set(topGenes.map(t => t.gene));
  toggledLabels.forEach(g => allLabeled.add(g));
  if (searchResult?.found) allLabeled.add(searchResult.gene);

  const allX = log2fc, allY = negLogFdr;
  const xPad = (Math.max(...allX) - Math.min(...allX)) * 0.05 || 1;
  const yPad = (Math.max(...allY) - Math.min(...allY)) * 0.05 || 1;
  const xDomain = [Math.min(...allX) - xPad, Math.max(...allX) + xPad];
  const yDomain = [Math.min(...allY) - yPad, Math.max(...allY) + yPad];

  let annotations = [...allLabeled].map(gene => {
    const d = compData.genes[gene];
    if (!d) return null;
    const y = -Math.log10(Math.max(d.fdr, 1e-16));
    return {
      x: d.log2FC, y,
      text: `<b>${gene}</b>`,
      showarrow: true, arrowhead: 0, arrowwidth: 1, arrowcolor: '#aaa',
      ax: 30, ay: -20,
      font: { size: 11, color: '#e8e8e8' },
      bgcolor: 'rgba(22,33,62,0.8)', borderpad: 2,
    };
  }).filter(Boolean);

  annotations = disperseAnnotations(annotations, plotWidth, plotHeight, margin, xDomain, yDomain);

  const compInfo = COMPARISONS.find(c => c.id === selectedComp);

  const layout = {
    width: plotWidth, height: plotHeight, margin,
    paper_bgcolor: '#1a1a2e', plot_bgcolor: '#16213e',
    font: { family: 'Inter, system-ui, sans-serif', color: '#e8e8e8' },
    title: { text: compInfo?.label || selectedComp, font: { size: 16 } },
    xaxis: { title: 'log\u2082 Fold Change', gridcolor: '#2a3a5e', zerolinecolor: '#888', range: xDomain },
    yaxis: { title: '-log\u2081\u2080 FDR', gridcolor: '#2a3a5e', zerolinecolor: '#888', range: yDomain },
    shapes: [
      { type: 'line', x0: -fcCutoff, x1: -fcCutoff, y0: yDomain[0], y1: yDomain[1], line: { color: '#555', width: 1, dash: 'dot' } },
      { type: 'line', x0: fcCutoff, x1: fcCutoff, y0: yDomain[0], y1: yDomain[1], line: { color: '#555', width: 1, dash: 'dot' } },
      { type: 'line', x0: xDomain[0], x1: xDomain[1], y0: -Math.log10(fdrCutoff), y1: -Math.log10(fdrCutoff), line: { color: '#555', width: 1, dash: 'dot' } },
    ],
    annotations,
    legend: { x: 1, y: 1, xanchor: 'right', bgcolor: 'rgba(22,33,62,0.8)', font: { size: 11 } },
    hovermode: 'closest', dragmode: 'zoom',
  };

  return (
    <div className="quadrant-container">
      <div className="quadrant-controls">
        <div className="quadrant-control-group">
          <label>Comparison</label>
          <select value={selectedComp} onChange={e => { setSelectedComp(e.target.value); setToggledLabels(new Set()); setSearchResult(null); }}>
            {COMPARISONS.filter(c => data.deResults[c.id]).map(c => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </div>

        <div className="quadrant-control-group">
          <label>|FC|</label>
          <input type="range" min="0" max="4" step="0.5" value={fcCutoff}
            onChange={e => setFcCutoff(parseFloat(e.target.value))} />
          <span>{fcCutoff}</span>
        </div>

        <div className="quadrant-control-group">
          <label>FDR</label>
          <select value={fdrCutoff} onChange={e => setFdrCutoff(parseFloat(e.target.value))}>
            {FDR_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>

        <div className="quadrant-control-group">
          <label>
            <input type="checkbox" checked={colorBySens} onChange={e => setColorBySens(e.target.checked)} />
            Color by EZH2i sensitivity
          </label>
        </div>

        <div className="quadrant-control-group volcano-search">
          <label>Gene</label>
          <div className="search-wrapper">
            <input
              ref={searchRef} type="text" value={searchQuery}
              onChange={handleSearchInput}
              onKeyDown={e => { if (e.key === 'Enter') doSearch(searchQuery); }}
              placeholder="Search..."
            />
            {suggestions.length > 0 && (
              <ul className="search-suggestions">
                {suggestions.map(g => <li key={g} onClick={() => { doSearch(g); searchRef.current?.blur(); }}>{g}</li>)}
              </ul>
            )}
          </div>
        </div>
      </div>

      <Plot
        data={traces} layout={layout}
        config={{ responsive: true, displayModeBar: true, modeBarButtonsToRemove: ['lasso2d', 'select2d'] }}
        onClick={handleClick}
      />
    </div>
  );
}
